import { catchError, shareReplay, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {User} from './user.model';
import { CommunicationService } from '../communication/communication.service';
import moment from 'moment';
import { Router } from '@angular/router';
import { ProjectCommunicationService } from '../communication/project-comms.service';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import Utils from '../util';
import { ToastrService } from 'ngx-toastr';
import { RxStompService } from '@stomp/ng2-stompjs';
import { environment } from '../../environments/environment';
import { jwtDecode } from 'jwt-decode';
import { DecodedToken, LoginData, PlatformFeatures } from '../models/auth/auth.model';

@Injectable()
export class AuthService {

  loggedInNotifier = new BehaviorSubject(false);

  constructor(
    private com: CommunicationService,
    private projectCom: ProjectCommunicationService,
    private router: Router,
    private rxStompService: RxStompService,
    private snackbarService: MatSnackBar,
    private toastrService: ToastrService,
    private dialog: MatDialog,
  ) {
    if (Utils.getExpiresAtCookie()) {
      const expiresAt = this.getExpiration();
      this.renewAt = expiresAt.subtract(this.renewalMinutesBeforeExpiration, 'minute');
    }
  }
  renewalInProgress = false;
  isLoggingOut = false;
  isLoggingIn = false;
  renewAt;
  renewalMinutesBeforeExpiration = 15;
  redirectTimeout?: ReturnType<typeof setTimeout>;

  login(user: User) {
    return (this.com.postRequestNoPopup(user, '/wsasapi/auth/login-data') as Observable<LoginData>).pipe(
      catchError((error: any) => {
        if (error.status === 403) {
          console.log('403 error occurred');
          Utils.createCookieThatExpiresInMinutes('unauthorized_access_attempt', 'AD•A', 1);
          window.location.href = environment.ssoHubUrl;
        }
        return [];
      }),
      tap(res => {
        this.setLoginData(res);
      }),
      shareReplay(), );
  }

  renewToken() {
    if (!this.renewalInProgress) {
      this.renewalInProgress = true;
      return (this.com.postRequestNoPopup({}, '/wsasapi/auth/renew') as Observable<{ token: string }>).pipe(
        tap((res) => {
          this.updateCookies(res.token);
          this.renewalInProgress = false;
        }));
    }
    return EMPTY;
  }

  getAccountInfo() {
    const id = localStorage.getItem('accountID');
    if (!id) {
      throw new Error('could not get account info');
    }
    return this.com.getRequest(id, '/wsasapi/accadmin/getAccountInfo');
  }

  private clearRedirectTimeout() {
    clearTimeout(this.redirectTimeout);
  }

  private setRedirectTimeout(seconds: number) {
    this.clearRedirectTimeout();
    this.redirectTimeout = setTimeout(() => {
      this.com.destroyMetricsNotifier.emit();
      this.logout();
    }, (seconds * 1000));
  }

  private updateCookies(encodedToken: string) {
    const decoded: DecodedToken = jwtDecode(encodedToken);
    localStorage.setItem('token', encodedToken);
    const expiresAt = moment().add(decoded.expiresIn, 'second');
    this.setRedirectTimeout(decoded.expiresIn);
    Utils.setCookies(encodedToken, expiresAt);
    this.renewAt = expiresAt.subtract(this.renewalMinutesBeforeExpiration, 'minute');
  }

  logout() {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    this.router.navigateByUrl('/logout').then(success => {
      if (success || this.router.url.includes('login')) {
        this.snackbarService.dismiss();
        this.dialog.closeAll();
        this.projectCom.unloadProject();
        this.clearRedirectTimeout();
        // Preserve user selected language after logging out
        const selectedLanguage = localStorage.getItem('language');
        localStorage.clear();
        if (selectedLanguage) {
          localStorage.setItem('language', selectedLanguage);
        }
        this.com.disconnectFromMetrics();
        this.com.disconnectFromSocket();
        this.loggedInNotifier.next(false);
      }
      setTimeout(() => {
        this.isLoggingOut = false;
      }, 0);
    });
  }

  public isLoggedIn() {
    // cookie will be undefined if it is expired
    const cookie = Utils.getAnessaTokenCookie();
    if (!cookie) {
      this.logout();
      return false;
    }

    if (this.hasNoLoginData()) {
      if (!this.isLoggingIn) {
        this.isLoggingIn = true;
        this.loginUsingToken(cookie);
      }
      return false;
    }

    if (this.tokenIsOutdated()) {
      localStorage.setItem('token', cookie);
      this.loginUsingToken(cookie);
    }

    return true;
  }

  private hasNoLoginData() {
    return !localStorage.getItem('userEmail') || localStorage.getItem('service') !== 'ADA';
  }

  private tokenIsOutdated() {
    const cookie = Utils.getAnessaTokenCookie();
    return localStorage.getItem('token') && localStorage.getItem('token') !== cookie;
  }

  /**
   * login using a token instead of a username + password
   * provides a persistent login when the user is redirected to a platform they were not previously logged in on
   */
  loginUsingToken(token: string) {
    const decoded: DecodedToken = jwtDecode(token);
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('service', 'ADA');
      localStorage.setItem('adoExpired', decoded.account.expirations.adoExpired + '');
      localStorage.setItem('admExpired', decoded.account.expirations.admExpired + '');

      const user = new User(
        decoded['user'].email,
        null,
        null,
        null,
        null,
        token,
        new Date
      );
      this.login(user).pipe(take(1)).pipe(catchError(err => {
        this.isLoggingIn = false;
        throw new Error('error in /login-data source ' + err);
      })).subscribe((data: any) => {
        console.log('subscribing to error socket');
        this.com.connectToSocket(this.rxStompService);
        this.com.connectToMetrics();
        this.com.subscribeToErrors(this.rxStompService);

        // Clear all toasts
        this.toastrService.clear();
        this.loggedInNotifier.next(true);
        this.router.navigateByUrl('/home');
        this.isLoggingIn = false;
      });
    }
  }

  isLoggedOut() {
    return !this.isLoggedIn();
  }

  getExpiration() {
    const expiration = Utils.getExpiresAtCookie();
    const expiresAt = JSON.parse(expiration);
    return moment(expiresAt);
  }

  shouldRenewToken(): boolean {
    if (this.isLoggedIn() && !this.renewalInProgress) {
      // check if 10 minutes or less are left on the current token
      const almostDone = moment().isAfter(this.renewAt);
      return almostDone;
    }
    // can't be a renew if there is no token
    return false;
  }

  private setLoginData(data: LoginData) {
    // set the local storage to null. remove old data
    localStorage.removeItem('accountID');
    localStorage.removeItem('accountName');
    localStorage.removeItem('userID');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userFirstName');
    localStorage.removeItem('userLastName');
    localStorage.removeItem('accountadmin');
    localStorage.removeItem('canViewHelp');
    localStorage.removeItem('dbadmin');
    localStorage.removeItem('checklistAccess');
    localStorage.removeItem('projectInputAccess');
    localStorage.removeItem('availableLanguages');
    localStorage.removeItem('userrole');
    localStorage.removeItem('limitToScreeningProjects');
    localStorage.removeItem('inputListAccess');
    localStorage.removeItem('screeningFormAccess');
    localStorage.removeItem('reachedProjectLimit');
    localStorage.removeItem('reachedScreeningProjectLimit');

    localStorage.setItem('accountID', data.accountId);
    localStorage.setItem('accountName', data.accountName);
    localStorage.setItem('userID', data.userId);
    localStorage.setItem('userEmail', data.userEmail);
    localStorage.setItem('userFirstName', data.firstName);
    localStorage.setItem('userLastName', data.lastName);
    localStorage.setItem('accountadmin', data.accountadmin + '');
    localStorage.setItem('dbadmin', data.dbadmin + '');
    localStorage.setItem('canViewHelp', data.canViewHelp + '');
    localStorage.setItem('checklistAccess', data.checklist_access + '');
    localStorage.setItem('projectInputAccess', data.project_input_access + '');
    localStorage.setItem('availableLanguages', data.languages + '');
    localStorage.setItem('isTranslator', data.translator + '');
    localStorage.setItem('userrole', data.userrole);
    localStorage.setItem('maintenance', data.maintenance + '');
    localStorage.setItem('limitToScreeningProjects', data.limitToScreeningProjects + '');
    localStorage.setItem('inputListAccess', data.input_list_access + '');
    localStorage.setItem('screeningFormAccess', data.screen_form_access + '');
    localStorage.setItem('reachedProjectLimit', data.reachedProjectLimit + '');
    localStorage.setItem('reachedScreeningProjectLimit', data.reachedScreeningProjectLimit + '');
    localStorage.setItem('canAccessADO', data.login_access.ado + '');
    localStorage.setItem('canAccessADO', data.login_access.ado + '');
    localStorage.setItem('canAccessADM', data.login_access.adm + '');

    this.processFeatures(data.features);

    if (data.maintenance === true) {
      this.router.navigateByUrl('/maintenance');
    }
  }

  private processFeatures(features: PlatformFeatures) {
    for (const feature of Object.keys(features)) {
      localStorage.setItem('features.' + feature, features[feature as keyof PlatformFeatures] + '');
    }
  }
}
